import React from "react";
import { Autocomplete, TextField, Typography } from "@mui/material";
import type { Job } from "../../../../../api/types";
import { getValidationErrorKey } from "../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../admin/ImportSettings";
import { ColumnContext } from "../standard-column-types";
import { useNextCropColumnViewModel } from "./NextCrop.column.vm";
import { useValidationErrors } from "../../ValidationErrorsContext";

export interface NextCropColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** Whether the field is required */
	required?: boolean;
	/** Whether the field is disabled */
	disabled?: boolean;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly NextCrop column component.
 * Uses the same viewmodel as the desktop version.
 */
export const NextCropColumnMobile: React.FC<NextCropColumnMobileProps> = ({
	job,
	columnContext,
	required = false,
	disabled = false,
	label,
}) => {
	const vm = useNextCropColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();
	const cropOptions = columnContext.props.nextCropOptions ?? [];

	const validationKey = getValidationErrorKey("nextCrop", job.id);
	const currentValue = vm.getCurrentValue(job);
	const showOtherMessage = currentValue === "Other";

	const handleChange = (_event: React.SyntheticEvent, newValue: string | null) => {
		if (disabled) return;
		vm.handleChange(job.id, newValue);
	};

	return (
		<>
			<Autocomplete
				size="small"
				fullWidth
				disabled={disabled}
				value={currentValue}
				options={cropOptions}
				getOptionLabel={(option) => option || ""}
				onChange={handleChange}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label || columnDisplayNames.nextCrop}
						required={required}
						error={!!validationErrors?.[validationKey]}
						helperText={showOtherMessage ? "Put type in Submission Notes" : validationErrors?.[validationKey]}
					/>
				)}
			/>
			{showOtherMessage && (
				<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
					Please put the crop type in Submission Notes
				</Typography>
			)}
		</>
	);
};
